import { useEffect, useRef, useState } from 'react';
import { Button, Card, Grid, Input, List, Table, Tooltip, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { PublicAuditTrailEntriesResult, PublicAuditTrailEntry } from '../../types/audit-trail.types';

const { Text } = Typography;

const MIN_SEARCH_LENGTH = 5;

interface PublicAuditTrailEntriesProps {
  entries: PublicAuditTrailEntriesResult | null;
  loading: boolean;
  canOpenDetail: boolean;
  onOpenDetail: (responseHash: string) => void;
  onPageChange: (page: number) => void;
  onSearch: (hashSearch: string) => void;
}

const formatDateTime = (value: string) => new Date(value).toLocaleString('pt-BR');

export default function PublicAuditTrailEntries({
  entries,
  loading,
  canOpenDetail,
  onOpenDetail,
  onPageChange,
  onSearch,
}: PublicAuditTrailEntriesProps) {
  const [inputValue, setInputValue] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);
  const screens = Grid.useBreakpoint();

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (inputValue.length === 0 || inputValue.length >= MIN_SEARCH_LENGTH) {
        onSearch(inputValue);
      }
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue]);

  const columns = [
    {
      title: 'Data/Hora',
      dataIndex: 'respondedAt',
      key: 'respondedAt',
      render: (value: string) => formatDateTime(value),
    },
    {
      title: 'Hash da Resposta',
      dataIndex: 'responseHash',
      key: 'responseHash',
      render: (value: string) => (
        <Tooltip title={value}>
          <Text code style={{ fontSize: 12 }}>{value.slice(0, 16)}…</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_: unknown, row: PublicAuditTrailEntry) =>
      canOpenDetail ? (
        <Button type="link" size="small" style={{ paddingInline: 0 }} onClick={() => onOpenDetail(row.responseHash)}>
          Ver detalhe
        </Button>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Disponível após encerramento
        </Text>
      ),
    },
  ];

  const isSearchTooShort = inputValue.length > 0 && inputValue.length < MIN_SEARCH_LENGTH;
  const isMobile = !screens.md;

  const searchInput = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Buscar por hash (mín. 5 caracteres)"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        status={isSearchTooShort ? 'warning' : undefined}
        style={isMobile ? { width: '100%' } : { width: 300 }}
      />
      {isSearchTooShort && (
        <Text type="warning" style={{ fontSize: 11 }}>
          Mínimo {MIN_SEARCH_LENGTH} caracteres para buscar
        </Text>
      )}
    </div>
  );

  const pagination = {
    current: entries?.page || 1,
    pageSize: 50,
    total: entries?.total || 0,
    onChange: (page: number) => onPageChange(page),
    showSizeChanger: false,
  };

  return (
    <Card
      title="Trilha de Respostas"
      extra={isMobile ? undefined : searchInput}
    >
      {isMobile && <div style={{ marginBottom: 12 }}>{searchInput}</div>}

      {isMobile ? (
        <List
          loading={loading}
          dataSource={entries?.items || []}
          pagination={pagination}
          rowKey="responseHash"
          renderItem={(item: PublicAuditTrailEntry) => (
            <List.Item
              style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}
              actions={[
                canOpenDetail ? (
                  <Button
                    type="link"
                    size="small"
                    style={{ paddingInline: 0 }}
                    onClick={() => onOpenDetail(item.responseHash)}
                  >
                    Ver detalhe
                  </Button>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Disponível após encerramento
                  </Text>
                ),
              ]}
            >
              <Text style={{ fontSize: 12 }}>{formatDateTime(item.respondedAt)}</Text>
              <Tooltip title={item.responseHash}>
                <Text code style={{ fontSize: 11 }}>{item.responseHash.slice(0, 20)}…</Text>
              </Tooltip>
            </List.Item>
          )}
        />
      ) : (
        <Table
          size="small"
          rowKey="responseHash"
          loading={loading}
          columns={columns}
          dataSource={entries?.items || []}
          pagination={pagination}
        />
      )}
    </Card>
  );
}
